import React from 'react'
import { withStyles } from '@mui/styles'
import { Stepper, Step, StepLabel } from '@mui/material'

import { hStepperStyles } from './horizontal-stepper.styles'

/**
 * @typedef {object} HorizontalStepperProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {React.ReactNode} children - El contenido que se muestra debajo del stepper (generalmente el contenido del paso actual).
 * @property {string[]} stepLabels - Array de etiquetas de texto para cada paso del stepper.
 * @property {number} currentStep - Índice (base 0) del paso actual activo.
 * @property {React.CSSProperties} [style] - Estilos CSS personalizados para el componente Stepper.
 */

/**
 * Un componente de stepper horizontal para flujos de múltiples pasos.
 *
 * Muestra un indicador de progreso visual con etiquetas para cada paso de un proceso secuencial.
 * El paso actual se destaca visualmente, y los pasos completados muestran una marca de verificación.
 * Ideal para formularios de múltiples pasos, procesos de registro, o flujos de trabajo guiados.
 *
 * @component
 * @example
 * const MyMultiStepForm = () => {
 *   const [currentStep, setCurrentStep] = useState(0);
 *   const steps = ['Información Personal', 'Dirección', 'Confirmación'];
 *
 *   const renderStepContent = () => {
 *     switch(currentStep) {
 *       case 0: return <PersonalInfoForm />;
 *       case 1: return <AddressForm />;
 *       case 2: return <ConfirmationStep />;
 *       default: return null;
 *     }
 *   };
 *
 *   return (
 *     <HorizontalStepper
 *       stepLabels={steps}
 *       currentStep={currentStep}
 *     >
 *       {renderStepContent()}
 *     </HorizontalStepper>
 *   );
 * };
 *
 * @param {HorizontalStepperProps} props - Las props del componente HorizontalStepper.
 */
export const HorizontalStepper = withStyles(hStepperStyles)(({
  classes,
  children,
  stepLabels,
  currentStep,
  style,
}) => {
  return (
    <div className={classes.root}>
      <Stepper activeStep={currentStep} style={style || {}} alternativeLabel>
        {stepLabels.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {children}
    </div>
  )
})
